import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Tag,
  Button,
  Spin,
  Empty,
  Tooltip,
  Typography,
  Card,
} from 'antd';
import { ShoppingOutlined } from '@ant-design/icons';
import { orderService } from '../../services/order.service';
import { OrderHistoryItem } from '../../types/order';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatters';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { vi } from 'date-fns/locale';

const { Title, Text } = Typography;

// Order status enum
const orderStatus = {
  Initialized: 'initialized',
  Confirmed: 'confirmed',
  Processing: 'processing',
  Shipping: 'shipping',
  Delivered: 'delivered',
  Cancelled: 'cancelled',
  Rejected: 'rejected',
  Completed: 'completed',
};

const OrderHistory: React.FC = () => {
  const [orders, setOrders] = useState<OrderHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation() as {
    t: (key: string) => string;
    i18n: any;
  };
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await orderService.getOrderHistory();
        setOrders(response.data || []);
      } catch (err) {
        console.error('Error fetching order history:', err);
        setError(t('errors.somethingWentWrong'));
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [t]);

  const getStatusColor = (status?: string) => {
    switch (status) {
      case orderStatus.Initialized:
        return 'default';
      case orderStatus.Confirmed:
        return 'processing';
      case orderStatus.Processing:
        return 'processing';
      case orderStatus.Shipping:
        return 'warning';
      case orderStatus.Delivered:
        return 'success';
      case orderStatus.Completed:
        return 'success';
      case orderStatus.Cancelled:
        return 'error';
      case orderStatus.Rejected:
        return 'error';
      default:
        return 'default';
    }
  };

  const formatOrderDate = (dateString?: string) => {
    if (!dateString) return '';

    try {
      const date = parseISO(dateString);
      const locale = i18n.language === 'vi' ? vi : undefined;
      return formatDistanceToNow(date, { addSuffix: true, locale });
    } catch (e) {
      return dateString;
    }
  };

  const formatMoneyWithLabel = (pricing?: {
    total_amount?: number;
    subtotal?: number;
    shipping_fee?: number;
  }) => {
    return (
      <>
        {t('order.subtotalLabel')}: {formatCurrency(pricing?.subtotal || 0)} +{' '}
        {t('order.shippingLabel')}: {formatCurrency(pricing?.shipping_fee || 0)}{' '}
        = <strong>{formatCurrency(pricing?.total_amount || 0)}</strong>
      </>
    );
  };

  const columns = [
    {
      title: t('order.orderCode'),
      dataIndex: 'order_code',
      key: 'order_code',
      render: (text?: string) => text || '-',
    },
    {
      title: t('order.orderTotal'),
      dataIndex: 'pricing',
      key: 'total_amount',
      render: (pricing?: {
        total_amount?: number;
        subtotal?: number;
        shipping_fee?: number;
      }) => formatMoneyWithLabel(pricing),
    },
    {
      title: t('order.orderDate'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date?: string) => (
        <Tooltip title={date}>{formatOrderDate(date)}</Tooltip>
      ),
    },
    {
      title: t('order.orderStatus'),
      dataIndex: 'status',
      key: 'status',
      render: (status?: string) => (
        <Tag color={getStatusColor(status)}>
          {status ? t(`order.${status}`) : '-'}
        </Tag>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size='large' />
        <div style={{ marginTop: '20px' }}>{t('order.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type='danger'>{error}</Text>
        <div style={{ marginTop: '20px' }}>
          <Button onClick={() => window.location.reload()}>
            {t('common.refresh')}
          </Button>
        </div>
      </div>
    );
  }

  if (!orders.length) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('order.noOrders')}
        >
          <Button
            type='primary'
            icon={<ShoppingOutlined />}
            onClick={() => navigate('/products')}
          >
            {t('cart.continueShopping')}
          </Button>
        </Empty>
      </Card>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8'>
      <Title level={2}>{t('order.orderHistory')}</Title>
      <Table
        columns={columns}
        dataSource={orders.map((order) => ({
          ...order,
          key: order.id || order.order_code,
        }))}
        pagination={{ pageSize: 10 }}
        rowKey={(record) =>
          record.id || record.order_code || Math.random().toString()
        }
      />
    </div>
  );
};

export default OrderHistory;
